// IE Hub - Enhanced Button Component
// Wrapper around MUI Button with IE Hub-specific enhancements

import React, { forwardRef, useMemo } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { DashboardService } from '../shared/api/services/dashboardService';
import { ActionType } from '../shared/api/types';

interface UIButtonProps {
  loading?: boolean;
  loadingText?: string;
  icon?: React.ReactNode;
  startIcon?: React.ReactNode;
  children?: React.ReactNode;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Optional logging context for UI event analytics */
  logAction?: {
    actionName?: string;
    pageName?: string;
    subSection?: string;
  };
  [key: string]: any;
}

const UIButton = forwardRef<HTMLButtonElement, UIButtonProps>(({ 
  loading = false,
  loadingText = 'Loading...',
  icon,
  startIcon,
  children,
  disabled,
  onClick,
  logAction,
  ...props
}, ref) => {
  const isDisabled = disabled || loading;
  
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (loading || disabled) {
      event.preventDefault();
      return;
    }

    // Fire-and-forget logging
    if (logAction || children) {
      const actionName = logAction?.actionName || (typeof children === 'string' ? children : 'Button Click');
      DashboardService.logButtonAction({
        actionType: ActionType.BUTTON_CLICK,
        actionName,
        pageName: logAction?.pageName || "Generic",
        subSection: logAction?.subSection || "Action",
      }).catch(() => {});
    }

    if (onClick) {
      onClick(event);
    }
  };

  // Compute the startIcon only when needed to avoid DOM conflicts with screen readers
  // Only pass startIcon prop when there's actually content to render
  const computedStartIcon = useMemo(() => {
    if (loading) {
      return <CircularProgress size={16} color="inherit" />;
    }
    // Return the explicit startIcon or icon, or undefined if neither exists
    return startIcon ?? icon ?? undefined;
  }, [loading, startIcon, icon]);

  // Only spread startIcon if it has a value to prevent MUI from creating empty wrapper
  const buttonProps = computedStartIcon !== undefined 
    ? { ...props, startIcon: computedStartIcon }
    : props;
  
  return (
    <Button data-testid="ui-uibutton-btn-handle-click"
      ref={ref}
      disabled={isDisabled}
      onClick={handleClick}
      {...buttonProps}
    >
      {loading ? loadingText : children}
    </Button>
  );
});

UIButton.displayName = 'UIButton';

export default UIButton;
